import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Card, Field, TextArea } from "@/components/admin/Primitives";

export default function Messages() {
  const [tab, setTab] = useState("email");
  const [emailForm, setEmailForm] = useState({ subject: "", html_content: "", recipients: "" });
  const [smsForm, setSmsForm] = useState({ message: "", recipients: "" });
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadLogs = () => api.get("/admin/messages/logs").then(({ data }) => setLogs(data));
  useEffect(() => { loadLogs(); }, []);

  const parseList = (txt) =>
    txt.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);

  const sendEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const recipients = emailForm.recipients ? parseList(emailForm.recipients) : null;
      const { data } = await api.post("/admin/messages/email", {
        subject: emailForm.subject,
        html_content: emailForm.html_content,
        recipients,
      });
      toast.success(`Sent ${data.sent}/${data.total} emails`);
      setEmailForm({ subject: "", html_content: "", recipients: "" });
      loadLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  const sendSms = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const recipients = smsForm.recipients ? parseList(smsForm.recipients) : null;
      const { data } = await api.post("/admin/messages/sms", { message: smsForm.message, recipients });
      toast.success(`Sent ${data.sent}/${data.total} SMS`);
      setSmsForm({ message: "", recipients: "" });
      loadLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-messages-page" className="text-white">
      <PageHeader title="Bulk Email & SMS" subtitle="Send announcements to your subscribers or a custom list." />

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("email")} data-testid="msg-tab-email" className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] font-bold inline-flex items-center gap-2 ${tab === "email" ? "bg-[var(--pz-red)] text-white" : "border border-[var(--pz-line)] text-zinc-400 hover:border-[var(--pz-red)]"}`}><Mail className="h-4 w-4" /> Email</button>
        <button onClick={() => setTab("sms")} data-testid="msg-tab-sms" className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] font-bold inline-flex items-center gap-2 ${tab === "sms" ? "bg-[var(--pz-red)] text-white" : "border border-[var(--pz-line)] text-zinc-400 hover:border-[var(--pz-red)]"}`}><MessageSquare className="h-4 w-4" /> SMS</button>
      </div>

      {tab === "email" ? (
        <Card>
          <form onSubmit={sendEmail} className="space-y-4">
            <Field label="Subject" value={emailForm.subject} onChange={(v) => setEmailForm({ ...emailForm, subject: v })} required testid="email-subject" />
            <TextArea label="HTML content" rows={8} value={emailForm.html_content} onChange={(v) => setEmailForm({ ...emailForm, html_content: v })} required testid="email-html" />
            <TextArea label="Recipients (comma/space/line separated). Leave blank to send to all subscribers." rows={3} value={emailForm.recipients} onChange={(v) => setEmailForm({ ...emailForm, recipients: v })} testid="email-recipients" />
            <div className="flex justify-end gap-2">
              <button disabled={busy} data-testid="send-email-btn" className="btn-red disabled:opacity-60"><Send className="h-4 w-4" /> {busy ? "Sending..." : "Send email"}</button>
            </div>
            <div className="text-xs text-zinc-500">Configure <b className="text-zinc-300">Brevo</b> or <b className="text-zinc-300">SMTP</b> in <a href="/admin/settings" className="text-[var(--pz-red)] underline">Settings → Email</a> (or set <code>RESEND_API_KEY</code> in backend env).</div>
          </form>
        </Card>
      ) : (
        <Card>
          <form onSubmit={sendSms} className="space-y-4">
            <TextArea label="Message (≤ 160 characters works best)" rows={4} value={smsForm.message} onChange={(v) => setSmsForm({ ...smsForm, message: v })} required testid="sms-message" />
            <TextArea label="Recipients (phone numbers, E.164 e.g. +94770000000). Leave blank to use subscriber phones." rows={3} value={smsForm.recipients} onChange={(v) => setSmsForm({ ...smsForm, recipients: v })} testid="sms-recipients" />
            <div className="flex justify-end gap-2">
              <button disabled={busy} data-testid="send-sms-btn" className="btn-red disabled:opacity-60"><Send className="h-4 w-4" /> {busy ? "Sending..." : "Send SMS"}</button>
            </div>
            <div className="text-xs text-zinc-500">Configure <b className="text-zinc-300">notify.lk</b> in <a href="/admin/settings" className="text-[var(--pz-red)] underline">Settings → SMS</a> (or fall back to Twilio env vars).</div>
          </form>
        </Card>
      )}

      <Card className="mt-6">
        <div className="font-display text-xl font-bold uppercase mb-3">Recent broadcasts</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-zinc-200">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              <tr><th className="px-3 py-2 font-bold">When</th><th className="px-3 py-2 font-bold">Type</th><th className="px-3 py-2 font-bold">Provider</th><th className="px-3 py-2 font-bold">Subject</th><th className="px-3 py-2 font-bold">Sent / Total</th></tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-[var(--pz-line)]/50">
                  <td className="px-3 py-2 text-zinc-500">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 uppercase font-bold">{l.type}</td>
                  <td className="px-3 py-2 text-zinc-400">{l.provider || "—"}</td>
                  <td className="px-3 py-2">{l.subject}</td>
                  <td className="px-3 py-2">{l.sent} / {l.recipients_count}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-500">No broadcasts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
